import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(theme => ({
  envVarsSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  envVarRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

interface QueueEnvVarRowProps {
  envKey: string;
  envValue: string;
  queueNames: string[];
  onUpdate: (key: string, value: string) => void;
  onDelete: (key: string) => void;
  onRename: (oldKey: string, newKey: string) => void;
}

function QueueEnvVarRow({
  envKey,
  envValue,
  queueNames,
  onUpdate,
  onDelete,
  onRename,
}: QueueEnvVarRowProps) {
  const classes = useStyles();
  const [localKey, setLocalKey] = useState(envKey);
  const keyUpdateTimeoutRef = useRef<NodeJS.Timeout>();
  const lastSavedKeyRef = useRef(envKey);
  const localKeyRef = useRef(localKey);
  const onRenameRef = useRef(onRename);

  useEffect(() => {
    localKeyRef.current = localKey;
  }, [localKey]);

  useEffect(() => {
    onRenameRef.current = onRename;
  }, [onRename]);

  useEffect(() => {
    setLocalKey(envKey);
    lastSavedKeyRef.current = envKey;
  }, [envKey]);

  useEffect(() => {
    return () => {
      if (keyUpdateTimeoutRef.current) {
        clearTimeout(keyUpdateTimeoutRef.current);
      }
      const trimmedKey = localKeyRef.current.trim();
      if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
        onRenameRef.current(lastSavedKeyRef.current, trimmedKey);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.envVarRow}>
      <TextField
        label="Key"
        value={localKey}
        onChange={e => {
          const newKey = e.target.value;
          setLocalKey(newKey);
          if (keyUpdateTimeoutRef.current) clearTimeout(keyUpdateTimeoutRef.current);
          keyUpdateTimeoutRef.current = setTimeout(() => {
            const trimmedKey = newKey.trim();
            if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
              onRename(lastSavedKeyRef.current, trimmedKey);
              lastSavedKeyRef.current = trimmedKey;
            }
          }, 300);
        }}
        onBlur={() => {
          if (keyUpdateTimeoutRef.current) clearTimeout(keyUpdateTimeoutRef.current);
          const trimmedKey = localKey.trim();
          if (trimmedKey !== lastSavedKeyRef.current && trimmedKey !== '') {
            onRename(lastSavedKeyRef.current, trimmedKey);
            lastSavedKeyRef.current = trimmedKey;
          } else if (trimmedKey === '') {
            setLocalKey(lastSavedKeyRef.current);
          }
        }}
        variant="outlined"
        size="small"
        style={{ flex: 1 }}
        placeholder="e.g., AWS__MY_QUEUE_URL"
      />
      <FormControl variant="outlined" size="small" style={{ flex: 2 }}>
        <InputLabel>Queue</InputLabel>
        <Select
          value={envValue}
          onChange={e => onUpdate(lastSavedKeyRef.current, e.target.value as string)}
          label="Queue"
        >
          <MenuItem value="">
            <em>Select queue</em>
          </MenuItem>
          {queueNames.map(q => (
            <MenuItem key={q} value={q}>
              {q}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton size="small" onClick={() => onDelete(lastSavedKeyRef.current)} color="secondary">
        <DeleteIcon />
      </IconButton>
    </div>
  );
}

interface ValkyrieQueueEnvVarsEditorProps {
  envVars: Record<string, string>;
  queueNames: string[];
  onUpdate: (v: Record<string, string>) => void;
  title?: string;
  description?: string;
}

export function ValkyrieQueueEnvVarsEditor({
  envVars,
  queueNames,
  onUpdate,
  title = 'Queue URLs (calculated at apply)',
  description = 'Key = env var name, value = queue (URL resolved at apply)',
}: ValkyrieQueueEnvVarsEditorProps) {
  const classes = useStyles();
  const vars = envVars ?? {};

  const handleUpdate = (key: string, value: string) => {
    onUpdate({ ...vars, [key]: value });
  };

  const handleAdd = () => {
    onUpdate({ ...vars, [`NEW_VAR_${Date.now()}`]: '' });
  };

  const handleDelete = (key: string) => {
    const next = { ...vars };
    delete next[key];
    onUpdate(next);
  };

  const handleRename = (oldKey: string, newKey: string) => {
    const next = { ...vars };
    const val = next[oldKey];
    delete next[oldKey];
    next[newKey] = val;
    onUpdate(next);
  };

  const canAdd = queueNames.length > 0;

  if (!canAdd) {
    return (
      <div className={classes.envVarsSection}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          {title}
        </Typography>
        <div className={classes.emptyState}>
          <Typography variant="body1">No queue URL environment variables configured</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Add at least one SQS queue in the SQS Queues tab to add queue URL environment variables.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.envVarsSection}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <Typography variant="subtitle2">{title}</Typography>
          {description && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontSize: '0.75rem', marginTop: 4 }}
            >
              {description}
            </Typography>
          )}
        </div>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Variable
        </Button>
      </div>
      {vars && Object.keys(vars).length > 0 ? (
        Object.entries(vars).map(([envKey, envValue]) => (
          <QueueEnvVarRow
            key={envKey}
            envKey={envKey}
            envValue={envValue}
            queueNames={queueNames}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onRename={handleRename}
          />
        ))
      ) : (
        <div className={classes.emptyState}>
          <Typography variant="body1">No queue URL environment variables configured</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Add a queue URL env var to map env var names to queues (URLs resolved at apply).
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            style={{ marginTop: 16 }}
          >
            Add First Variable
          </Button>
        </div>
      )}
    </div>
  );
}
